import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';
import { fillGradient } from './helpers/gradient';
import { getStrokeBounds, translatePoints } from './helpers/points';
import { calculatePathDistances, calculatePath, interpolatePointsUniform } from './helpers/points';

const defaultBrushSettings = brushDefaults.velvetPastel || {};

// Быстрый детерминированный RNG на базе 32-битного линейного конгруэнтного генератора.
// Важно: мы НЕ держим один общий поток. Вместо этого создаём локальные RNG по индексам через seed-деривацию.
// Так любая "случайность" адресуется функцией от (seedBase, indices...), остаётся неизменной при добавлении/смещении.
class RNG {
  constructor(seed = 1) {
    const s = (seed * 0x9e3779b1) >>> 0;
    this.state = (s ^ 0x9e3779b9) >>> 0;
  }
  next() {
    this.state = (1664525 * this.state + 1013904223) >>> 0;
    return (this.state >>> 0) * 2.3283064365386963e-10;
  }
  range(a = 0, b = 1) { return a + (b - a) * this.next(); }
  signed(w = 1) { return (this.next() - 0.5) * 2 * w; }
}

// Небыстрый, но простой 32-битный "хэш" для деривации сидов
function hash32(a) {
  a |= 0;
  a = (a ^ (a >>> 16)) >>> 0;
  a = Math.imul(a, 0x7feb352d) >>> 0;
  a = (a ^ (a >>> 15)) >>> 0;
  a = Math.imul(a, 0x846ca68b) >>> 0;
  a = (a ^ (a >>> 16)) >>> 0;
  return a >>> 0;
}

// Комбайнер нескольких целочисленных величин в один 32-битный seed
function seedCombine(...nums) {
  let s = 0x9e3779b9 >>> 0;
  for (let i = 0; i < nums.length; i++) {
    const v = (nums[i] | 0) >>> 0;
    s = hash32(s ^ v);
  }
  return s >>> 0;
}

// Утилиты
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

function fastAverageNum(arr, key) {
  let s = 0;
  const n = arr.length;
  for (let i = 0; i < n; i++) s += arr[i][key] || 0;
  return n ? s / n : 0;
}

// Малокистевые кривые плотности (сохраняем поведение)
function smallBrushDensityCurve(lineWidth, baseScaleFactor) {
  if (lineWidth >= baseScaleFactor) return 1;
  const r = clamp(lineWidth / baseScaleFactor, 0.1, 1);
  return r * r;
}

function extraSmallDensityMul(lineWidth, baseScaleFactor, extraFactor = 1.0) {
  if (lineWidth >= baseScaleFactor) return 1;
  const r = clamp(lineWidth / baseScaleFactor, 0, 1);
  const p = 1.5;
  const drop = Math.pow(1 - r, p);
  const mul = 1 - clamp(extraFactor, 0, 2) * drop;
  return clamp(mul, 0.15, 1);
}

// Предрасчёт траектории (как было)
function precomputeTrajectory(points, lineWidth, cfg) {
  const {
    proportionalTail = true,
    tailLength = 2.0,
    tailRelativeLength = 0.5,
    weight,
    baseScaleFactor,
  } = cfg;

  const n = points.length;
  if (n === 0) return { tPoints: [], totalPath: 0 };
  const totalPath = calculatePath(points);
  const pathPts = calculatePathDistances(points);

  const out = new Array(n);

  for (let i = 0; i < n; i++) {
    const p = pathPts[i];
    const fromStart = p.distance;
    const fromEnd = totalPath - p.distance;
    const minD = fromStart < fromEnd ? fromStart : fromEnd;

    let baseTaper = weight;
    if (proportionalTail) {
      const eff = Math.min(totalPath, tailRelativeLength * totalPath);
      if (eff > 0 && minD < eff) {
        const t = minD / eff;
        baseTaper = t * t * (3 - 2 * t) * weight;
      }
    } else {
      const eff = Math.min(totalPath, Math.max(lineWidth, baseScaleFactor / 3) * tailLength);
      
      if (eff > 0 && minD < eff) {
        const t = minD / eff;
        baseTaper = t * t * (3 - 2 * t) * weight;
      }
    }
    const press = p.pressure ? p.pressure * 1.5 : baseTaper;

    let dx = 0, dy = 0;
    if (i > 0) { dx += p.x - pathPts[i - 1].x; dy += p.y - pathPts[i - 1].y; }
    if (i < n - 1) { dx += pathPts[i + 1].x - p.x; dy += pathPts[i + 1].y - p.y; }

    const sp = Math.hypot(dx, dy);
    const ang = Math.atan2(dy, dx);

    const tx = Math.cos(ang);
    const ty = Math.sin(ang);
    const nx = -ty;
    const ny = tx;

    out[i] = {
      x: p.x, y: p.y,
      distance: p.distance, pathRatio: p.pathRatio,
      pressure: press, tail: press,
      angle: ang, speed: sp,
      tx, ty, nx, ny,
    };
  }

  return { tPoints: out, totalPath };
}

// Генератор эллиптических оффсетов: детерминированен по seed и индексу гранулы
function generatePowderOffsetsDeterministic(seedBase, lineWidth, count, ovality = 1.2, granuleSize = -0.2) {
  const a = lineWidth * 0.5 * ovality;
  const b = lineWidth * 0.5 / ovality;
  const ax2 = a * a;
  const by2 = b * b;

  const offsets = new Array(count);
  for (let i = 0; i < count; i++) {
    // Локальный RNG от seedBase и индекса гранулы
    const rng = new RNG(seedCombine(seedBase, 0xA1B2C3D4, i));
    const r = Math.sqrt(rng.next());
    const theta = rng.range(0, Math.PI * 2);
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);

    const ex = r * cosT * a;
    const ey = r * sinT * b;

    const edge = Math.min(1, Math.sqrt((ex * ex) / ax2 + (ey * ey) / by2));
    const baseSize = 0.4 + (1 - edge) * 0.6;
    const size = Math.max(0.18, (baseSize + granuleSize));

    offsets[i] = { x: ex, y: ey, edge, size, i }; // фиксируем индекс
  }
  return offsets;
}

// Цветовые группы: детерминированная "случайность" по seedBase и индексу гранулы
function buildColorGroupsDeterministic(seedBase, {
  chromaColor, gradientColor, opacity, centerAlphaCut, smallBrushFade,
}, offsets) {
  const groups = new Map();

  const baseHex = chromaColor.hex();

  for (let i = 0; i < offsets.length; i++) {
    const o = offsets[i];

    let colorHex;
    const edgeFade = 1 - (1 - o.edge) * centerAlphaCut;
    const smallEdgeFade = 1 - smallBrushFade * (o.edge);
    const granAlpha = opacity * 0.6 * edgeFade * smallEdgeFade;
    colorHex = chroma(baseHex).alpha(granAlpha).hex();

    if (!groups.has(colorHex)) groups.set(colorHex, []);
    groups.get(colorHex).push(o);
  }

  return groups;
}

export async function drawVelvetPastelStroke({
  color,
  gradientColor,
  lineWidth,
  points,
  time,
  sets = {},
}, context) {
  if (!points || points.length === 0) return;

  const cfg = Object.assign({}, defaultBrushSettings, sets);

  const {
    opacity = 0.55,
    weight = 1,
    density = 1.0,
    granuleSkipping = 0.3,
    granuleSize = -0.28,
    baseScaleFactor = 60,
    jitter = 0,

    proportionalTail = false,
    tailLength = 2.0,
    tailRelativeLength = 0.5,

    softCore = 0.4,
    centerSoftness = 0.8,
    centerAlphaCut = 0.16,
    coreRadiusGrow = 0.9,
    extraSmallDensityFactor = 0,
    
  } = cfg;

  const {
    smear = 0.16,
    corePack = 0.7,

    ovality = 1.25,
    maxOffsetsBase = 900,
    powder = 0.9,

    splinterRate = 0.45,
    splinterLenMax = 0.14,
    splinterAngleJitter = 0.18,
    speedDownsample = 0.6,
    pressureDownsample = 0.5,
  } = cfg;

  // ВАЖНО: базовый сид зависит только от time
  // Приводим time к миллисекундам, округляем для стабильности
  const timeMs = Math.floor((time || 0) * 1000);
  const seedBase = seedCombine(
    0x5EED0001,
    timeMs | 0,
  );

  const chromaColor = chroma(color);
  const originalAlpha = chromaColor.alpha();

  // Bounds используются только для буфера и translate, не для RND
  const bounds = getStrokeBounds(points, lineWidth, 0, 150, 150);
  if (!bounds) return;

  // Переводим для рисования в локальные координаты, но RNG не зависит от этого
  const ox = -bounds.x;
  const oy = -bounds.y;
  const pointsLoc = translatePoints(points, ox, oy);

  // Немного реже интерполируем при маленьких кистях
  const interpStep = Math.max(1, lineWidth / 12);
  const interpPoints = interpolatePointsUniform(pointsLoc, interpStep);

  const { tPoints, totalPath } = precomputeTrajectory(interpPoints, lineWidth, {
    proportionalTail, tailLength, tailRelativeLength, weight, baseScaleFactor,
  });

  const bufferCanvas = document.createElement('canvas');
  bufferCanvas.width = bounds.width;
  bufferCanvas.height = bounds.height;
  const ctx = bufferCanvas.getContext('2d');
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const isSmall = lineWidth < baseScaleFactor;
  const smallNorm = clamp((baseScaleFactor - lineWidth) / baseScaleFactor, 0, 1);

  // Средние характеристики (не влияют на RND, только на детерминированные масштабы)
  const avgSpeed = fastAverageNum(tPoints, 'speed');
  const avgPress = fastAverageNum(tPoints, 'pressure') || 1;
  const speedFactor = 1 / (1 + speedDownsample * (avgSpeed / Math.max(1, lineWidth)));
  const pressFactor = 0.6 + 0.4 * avgPress;
  const densityMulDynamic = clamp(speedFactor * (0.6 + pressureDownsample * pressFactor), 0.35, 1.0);

  // Базовая плотность
  let densityBase = density * densityMulDynamic;

  // Коррекция плотности для маленьких кистей
  if (isSmall) {
    const curveMul = smallBrushDensityCurve(lineWidth, baseScaleFactor);
    const extraMul = extraSmallDensityMul(lineWidth, baseScaleFactor, extraSmallDensityFactor);
    densityBase *= curveMul * extraMul;
  }

  // Ограничитель количества гранул — без зависимости от длины штриха, чтобы избежать "плясок"
  const maxOffsets = Math.max(200, Math.round(maxOffsetsBase * (isSmall ? 0.7 : 1.0)));

  // Число гранул — фиксированная функция плотности (не зависит от bounds и текущего прогресса)
  const baseDotCount = Math.min(maxOffsets, Math.round(1200 * densityBase));

  // Детерминированные оффсеты гранул
  const dotOffsets = generatePowderOffsetsDeterministic(
    seedBase, lineWidth, baseDotCount, ovality, granuleSize
  );

  // Малокистевые корректировки (детерминированные коэффициенты)
  const jitterShrink = 1 - 0.25 * smallNorm;
  const smallBrushFade = 0.22 * smallNorm;

  // Цветовые группы (детерминированно от seedBase)
  const groups = buildColorGroupsDeterministic(seedBase, {
    chromaColor, gradientColor, opacity, centerAlphaCut, smallBrushFade,
  }, dotOffsets);

  const jitterScale = Math.max(0.6, lineWidth * 0.22 * jitter * jitterShrink);

  const coreTightness = 0.5 + corePack * 0.5;
  const coreRadiusMul = 1 + coreRadiusGrow * 0.6;

  const smearK = lineWidth * 0.18;
  const splMaxBase = lineWidth * splinterLenMax * (1 - 0.35 * smallNorm);

  ctx.globalAlpha = 1;


  const rngForPointGranule = (pi, gi) => new RNG(seedCombine(seedBase, 0x33333333, pi, gi));

  for (const [gColor, gOffsets] of groups.entries()) {
    ctx.fillStyle = gColor;
    ctx.beginPath();

    const splinters = [];

    for (let k = 1; k < tPoints.length; k++) {
      const prev = tPoints[k - 1];
      const curr = tPoints[k];

      const tx = curr.tx, ty = curr.ty;
      const nx = curr.nx, ny = curr.ny;

      const tailAvg = (prev.tail + curr.tail) * 0.5;
      const lowPress = (1 - tailAvg);

      const smearTailMod = isSmall ? (1 - 0.5 * lowPress) : 1;
      const smearShift = smear * lowPress * smearTailMod;
      const sxBase = tx * smearShift * smearK;
      const syBase = ty * smearShift * smearK;

      const jBaseMul = (0.6 + 0.4 * (1 - tailAvg));
      const sinkBaseK = (0.15 + 0.35 * coreTightness);
      const coreMul = 0.85 + 0.15 * coreRadiusMul;

      for (let i = 0, L = gOffsets.length; i < L; i++) {
        const gi = gOffsets[i].i;
        const off = gOffsets[i];
        const edge = off.edge;

        // Для пары (k, gi) используем отдельный RNG, чтобы порядок циклов/пропуски не влияли
        const rngPG = rngForPointGranule(k, gi);

        // Пропуски гранул — стабилен по грануле с лёгкой модификацией от точки
        const skipRoll = rngPG.next();
        const granSkip = granuleSkipping > 0 && (skipRoll < (granuleSkipping));
        if (granSkip) continue;

        // Пропуск у края и в хвостах
        const skipChance =
          powder * (0.16 + 0.5 * edge) * (1 - tailAvg) * (isSmall ? (1.15 + 0.3 * edge) : 1);
        if (rngPG.next() < skipChance) continue;

        // Оффсет эллипса в базисе касательной/нормали
        const offX = (nx * off.x + tx * off.y) * coreMul;
        const offY = (ny * off.x + ty * off.y) * coreMul;

        const sink = (1 - edge) * sinkBaseK * (isSmall ? 0.85 : 1);
        const centerPullX = -nx * sink * off.x * 0.25;
        const centerPullY = -ny * sink * off.y * 0.25;

        // Джиттер — стабильный для пары (k, gi)
        const jx = rngPG.signed(jitterScale * jBaseMul) * 0.5;
        const jy = rngPG.signed(jitterScale * jBaseMul) * 0.5;

        const p0x = prev.x + offX + jx + sxBase + centerPullX;
        const p0y = prev.y + offY + jy + syBase + centerPullY;

        // Размер гранулы
        const centerBoost = 1 - edge;
        const centerScale = 1 - centerSoftness * centerBoost;
        const localScale = tailAvg * (1 - softCore * 0.45) * (0.9 + 0.2 * (1 - edge)) * centerScale;

        let minDot, baseDot, upperClamp;
        if (isSmall) {
          minDot = Math.max(0.18, baseScaleFactor * 0.006);
          const baseDot60 = baseScaleFactor * 0.045 * localScale * (1 + off.size * 0.25);
          baseDot = baseDot60;
          upperClamp = Math.max(lineWidth * 0.08, baseScaleFactor * 0.08);
        } else {
          minDot = Math.max(0.18, lineWidth * 0.006);
          baseDot = lineWidth * 0.045 * localScale * (1 + off.size * 0.25);
          upperClamp = lineWidth * 0.08;
        }
        const localWidth = clamp(baseDot, minDot, upperClamp);

        // Решение о "щепке" (splinter) — стабильно по (k, gi)
        const doSplinter = rngPG.next() < (splinterRate * (0.6 + 0.4 * centerBoost));

        if (!doSplinter) {
          ctx.moveTo(p0x + localWidth, p0y);
          ctx.arc(p0x, p0y, localWidth * 0.52, 0, Math.PI * 2);
        } else {
          const aj = rngPG.signed(splinterAngleJitter);
          const sTx = Math.cos(curr.angle + aj);
          const sTy = Math.sin(curr.angle + aj);

          const maxLen = Math.max(0.35, splMaxBase * (0.7 + 0.3 * (1 - edge)));
          const localLen = Math.min(
            maxLen,
            Math.max(0.36, lineWidth * 0.09 * localScale * (0.9 + 0.2 * (1 - edge)))
          );

          const p1x = p0x + sTx * localLen;
          const p1y = p0y + sTy * localLen;

          // Средняя точка кривой — добавляем поперечный разброс, стабильный по (k, gi)
          const midx = (p0x + p1x) * 0.5 + nx * localWidth * 0.25 * rngPG.signed(1);
          const midy = (p0y + p1y) * 0.5 + ny * localWidth * 0.25 * rngPG.signed(1);

          splinters.push({ p0x, p0y, p1x, p1y, midx, midy, w: Math.max(localWidth * 0.9, minDot) });
        }
      }
    }

    ctx.fill();

    if (splinters.length) {
      const buckets = new Map();
      for (let i = 0; i < splinters.length; i++) {
        const s = splinters[i];
        const wRounded = Math.max(0.4, Math.round(s.w * 2) / 2);
        const key = wRounded.toString();
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(s);
      }

      ctx.strokeStyle = gColor;

      for (const [key, arr] of buckets.entries()) {
        const w = parseFloat(key);
        ctx.lineWidth = w;
        ctx.beginPath();
        for (let i = 0; i < arr.length; i++) {
          const s = arr[i];
          ctx.moveTo(s.p0x, s.p0y);
          ctx.quadraticCurveTo(s.midx, s.midy, s.p1x, s.p1y);
        }
        ctx.stroke();
      }
    }
  }

  if (gradientColor) {
    fillGradient(ctx, tPoints, color, gradientColor, lineWidth);
    context.globalAlpha = 1;
  } else {
    context.globalAlpha = originalAlpha;
  }

  context.drawImage(
    bufferCanvas,
    0, 0, bounds.width, bounds.height,
    bounds.x, bounds.y, bounds.width, bounds.height
  );
  context.globalAlpha = 1;
}
